import { FC, useMemo } from 'react';
import styled from 'styled-components';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { CircularLoader } from '../../../shared';
import { useItemThumbnail } from '../../../hooks';
import { useGetPresentationThemesQuery } from '../../../services';
import { SlideItem } from './SlideItem';
import { LayoutCompatibilityBadge } from './LayoutCompatibilityBadge';
import { getLayoutInSelectedTheme } from './folderManagerGrid.shared';

const ContentContainer = styled.div`
  padding: 28px 32px 40px;
  overflow-y: auto;
  height: 100%;
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const PreviewSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const UnthemedPreviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const ThemePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 28px 32px;
  align-items: start;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
`;

const ThemePreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 245px;
`;

const PreviewImageWrapper = styled.div`
  position: relative;
  width: 245px;
  height: 138px;
  border-radius: ${({ theme }) => theme.radius.default};
`;

const BadgeCorner = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  border-radius: 999px;
`;

const PreviewDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.default};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.black};
`;

const PreviewSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.default};
  color: ${({ theme }) => theme.palette.gray};
`;

const getSlideLayoutName = (item: FolderManagerItem): string | null =>
  item.itemData?.slideData?.LayoutName ||
  item.itemData?.LayoutName ||
  null;

const getSlideNormalizedLayoutName = (item: FolderManagerItem): string | null =>
  item.itemData?.slideData?.NormalizedLayoutName ||
  item.itemData?.NormalizedLayoutName ||
  null;

const getDefaultLayoutName = (themeLayouts?: PresentationThemeLayout[]): string | null =>
  themeLayouts?.find((layout) => layout.index === 1)?.name || null;

const getAppliedLayoutCopy = (
  item: FolderManagerItem,
  themeLayouts?: PresentationThemeLayout[],
  themeId?: number | null,
): string => {
  const supportLevel = getLayoutInSelectedTheme(item, themeId);
  const layoutName = getSlideLayoutName(item) || 'Unknown layout';
  const normalizedLayoutName = getSlideNormalizedLayoutName(item);
  const defaultLayoutName = getDefaultLayoutName(themeLayouts);

  switch (supportLevel) {
    case 'FULLY_SUPPORTED':
      return `Applied layout: ${layoutName}`;
    case 'PARTIALLY_SUPPORTED':
      return normalizedLayoutName ? `Applied layout: ${layoutName} → ${normalizedLayoutName}` : `Applied layout: ${layoutName}`;
    case 'NOT_SUPPORTED':
      return `Default layout fallback (${defaultLayoutName || 'Unknown layout'})`;
    default:
      return `Applied layout: ${layoutName}`;
  }
};

const SlideThemeThumbnailCard: FC<{
  item: FolderManagerItem;
  themeId?: number | null;
  themeName: string;
  themeLayouts?: PresentationThemeLayout[];
  isUnthemed?: boolean;
}> = ({ item, themeId, themeName, themeLayouts, isUnthemed = false }) => {
  const { thumbnailUrl, isLoading, isFetching, isThemeChanging } = useItemThumbnail({
    itemId: item?.id,
    itemType: item?.type,
    thumbnailUrl: item?.thumbnailUrl,
    themeId: themeId ?? null,
  });
  const slideLayoutName = getSlideLayoutName(item);
  const supportLevel = getLayoutInSelectedTheme(item, themeId);
  const normalizedLayoutName = getSlideNormalizedLayoutName(item);
  const defaultLayoutName = getDefaultLayoutName(themeLayouts);


  return (
    <ThemePreviewCard>
      {isLoading || (isThemeChanging && isFetching) ? (
        <LoaderContainer>
          <CircularLoader size={24} />
        </LoaderContainer>
      ) : (
        <PreviewImageWrapper>
          {!isUnthemed && supportLevel && (
            <BadgeCorner>
                <LayoutCompatibilityBadge
                  successTooltipTitle={`Theme fully supports layout "${slideLayoutName}".`}
                  warningTooltipTitle={`Theme partially supports layout "${slideLayoutName}", applied layout "${normalizedLayoutName}".`}
                  errorTooltipTitle={`Theme does not support layout "${slideLayoutName}", applied default layout "${defaultLayoutName || 'unknown'}".`}
                  layoutName={slideLayoutName}
                  supportLevel={supportLevel} 
                  hideLabel />
            </BadgeCorner>
          )}
          <SlideItem thumbnailUrl={thumbnailUrl} />
        </PreviewImageWrapper>
      )}

      <PreviewDetails>
        <PreviewTitle>{themeName}</PreviewTitle>
        {isUnthemed ? (
          <PreviewSubtitle>Layout: {slideLayoutName}</PreviewSubtitle>
        ) : (
          <PreviewSubtitle>{getAppliedLayoutCopy(item, themeLayouts, themeId)}</PreviewSubtitle>
        )}
      </PreviewDetails>
    </ThemePreviewCard>
  );
};

interface SlideThemePreviewDialogProps {
  item: FolderManagerItem;
  open: boolean;
  onClose: () => void;
}

export const SlideThemePreviewDialog: FC<SlideThemePreviewDialogProps> = ({ item, open, onClose }) => {
  const { data: presentationThemes, isLoading } = useGetPresentationThemesQuery();

  const themes = useMemo(() => {
    if (!presentationThemes?.length) {
      return [];
    }

    return [...presentationThemes].sort((a, b) => {
      if (a.IsDefault !== b.IsDefault) {
        return a.IsDefault ? -1 : 1;
      }

      return a.Name.localeCompare(b.Name);
    });
  }, [presentationThemes]);

  return (
    <Dialog fullScreen open={open} onClose={onClose} sx={{ zIndex: 99999 }}>
      <AppBar sx={{ position: 'relative', background: 'white' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon style={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }} style={{ color: 'black' }}>
            {`${item?.name || item?.displayName || 'Slide'} Theme Previews`}
          </Typography>
        </Toolbar>
      </AppBar>

      <ContentContainer>
        {isLoading ? (
          <LoaderContainer>
            <CircularLoader size={24} />
          </LoaderContainer>
        ) : themes.length > 0 ? (
          <SectionsContainer>
            <PreviewSection>
              <SectionHeading>
                <SectionTitle>Unthemed Slide</SectionTitle>
              </SectionHeading>
              <UnthemedPreviewRow>
                <SlideThemeThumbnailCard item={item} themeName="None" isUnthemed />
              </UnthemedPreviewRow>
            </PreviewSection>

            <PreviewSection>
              <SectionHeading>
                <SectionTitle>Theme Previews</SectionTitle>
              </SectionHeading>

              <ThemePreviewGrid>
                {themes.map((theme) => (
                  <SlideThemeThumbnailCard
                    key={theme.ID}
                    item={item}
                    themeId={theme.AssetVersionID}
                    themeLayouts={(theme as any).Layouts}
                    themeName={
                      theme.Name || (theme as any)?.AssetVersion?.Asset?.DisplayName || `Theme ${theme.AssetVersionID}`
                    }
                  />
                ))}
              </ThemePreviewGrid>
            </PreviewSection>
          </SectionsContainer>
        ) : (
          <p>No presentation themes available.</p>
        )}
      </ContentContainer>
    </Dialog>
  );
};
